import { useCallback, useEffect, useRef } from "react";
import { useLocation, useRoute } from "wouter";
import type { DecideSubview, RailMode, WorkspaceTab } from "./types";

// One pattern, two consumers (the route-state hook and the visibility hook
// below) — the workspace path layout must not drift between them.
const FUNDER_WORKSPACE_ROUTE = "/funder/:tab/:rail?/:itemId?";

const URL_RAIL_TO_INTERNAL: Record<string, RailMode> = {
  overview: "overview",
  tracks: "tracks",
  proposals: "orgs",
  recommenders: "recs",
};
const INTERNAL_RAIL_TO_URL: Record<RailMode, string> = {
  overview: "overview",
  tracks: "tracks",
  orgs: "proposals",
  recs: "recommenders",
};

// Absolute path to a funder "Review" rail (optionally a specific item),
// derived from the canonical rail→URL-segment map so external deep links (e.g.
// the Saved drawer) can't drift from the routing layer. The `~/` prefix resets
// to the round root under wouter's nested routing.
export function funderReviewPath(
  roundId: string,
  rail: RailMode,
  itemId?: string | null,
): string {
  const base = `~/round/${roundId}/funder/review/${INTERNAL_RAIL_TO_URL[rail]}`;

  return itemId == null ? base : `${base}/${itemId}`;
}

// Absolute path to a funder "Decide" subview — the sibling of
// `funderReviewPath` for the workspace's other tab. Same `~/` prefix, same
// reason: an external link into Decide (the To Do rail's scoring and
// evaluation steps) must be built from the routing layer rather than by
// string-concatenating the segment at each call site.
export function funderDecidePath(
  roundId: string,
  subview: DecideSubview,
): string {
  return `~/round/${roundId}/funder/decide/${subview}`;
}

// Real browser URL for a funder "Review" rail, suitable for an `<a href>`
// (no wouter `~/` prefix). Use this when a plain anchor needs a navigable
// target — e.g. so middle/cmd-click can open the rail in a new tab.
export function funderReviewHref(
  roundId: string,
  rail: RailMode,
  itemId?: string | null,
): string {
  const base = `/round/${roundId}/funder/review/${INTERNAL_RAIL_TO_URL[rail]}`;

  return itemId == null ? base : `${base}/${itemId}`;
}

// Real browser URL for a funder "Decide" subview — the sibling of
// `funderReviewHref`, for the same reason: a plain anchor target so
// cmd/ctrl/middle-click can open the subview in a new tab.
export function funderDecideHref(
  roundId: string,
  subview: DecideSubview,
): string {
  return `/round/${roundId}/funder/decide/${subview}`;
}

// Normalize URL segments and persisted History targets at the compatibility
// boundary. Current navigation emits only `note-ratings`; the two older names
// remain readable so existing bookmarks and History rows still resolve.
export function normalizeFunderDecideSubview(
  value: string | null | undefined,
): DecideSubview | null {
  if (value === "incremental-evals") return "incremental-evals";
  if (
    value === "note-ratings" ||
    value === "notes-ratings" ||
    value === "ratings"
  ) {
    return "note-ratings";
  }
  return null;
}

// True when a workspace URL carries a deep link whose destination lives
// inside the rail content — such content cannot wait behind the rail panel's
// after-paint placeholder: reveal/landing registration only exists once the
// panel mounts, and the deferred mount rides a low-priority transition that a
// busy cold load (corpus downloads, search indexing, catalog queries all
// resolving at once) can starve for seconds, leaving the flat placeholder up
// instead of the panel's real loading skeletons until everything has arrived
// (SP-2615). Recognized targets:
//   - a passage/chapter hash (`#s=…`)
//   - `?quote=` (find-and-highlight a passage)
//   - `?proposal=` (auto-open a recommender's evaluation)
//   - `?landing=` (explicit semantic section target, e.g. the Saved drawer)
//   - `?transcript=` (transcript search hits / chapter links picking a meeting)
// Any new deep-link param whose target renders inside the rail content must
// be added here — see the corollary in `pages/round/CLAUDE.md`.
export function workspaceUrlNeedsEagerRailContent(
  locationKey: string,
): boolean {
  const url = new URL(locationKey, "http://localhost");
  return (
    url.hash.length > 1 ||
    url.searchParams.has("quote") ||
    url.searchParams.has("proposal") ||
    url.searchParams.has("landing") ||
    url.searchParams.has("transcript")
  );
}

// True while the funder Review workspace is the visible tab — render-phase
// behavior that must not run in the hidden-under-<Activity> Review panels
// (e.g. seeding clip autoplay) gates on this instead of prop-drilling the
// tab. Returns true outside workspace routes, leaving other surfaces alone.
export function useIsReviewWorkspaceVisible(): boolean {
  const [, params] = useRoute<{ tab: string }>(FUNDER_WORKSPACE_ROUTE);
  return params?.tab !== "decide";
}

export function useFunderWorkspaceRouteState() {
  // URL-driven view state. Path layout:
  //   /funder/<tab>/<rail>/<itemId>
  //   tab    = "review" | "decide"
  //   rail   = "overview" | "proposals" | "recommenders"  (Review only)
  //   itemId = trackId / proposalId / recommenderId, scoped to rail
  const [, setLocation] = useLocation();
  const [, params] = useRoute<{
    tab: string;
    rail?: string;
    itemId?: string;
  }>(FUNDER_WORKSPACE_ROUTE);

  const workspaceTab: WorkspaceTab =
    params?.tab === "decide" ? "decide" : "review";

  const urlRail = params?.rail;
  const railMode: RailMode =
    urlRail != null && urlRail in URL_RAIL_TO_INTERNAL
      ? URL_RAIL_TO_INTERNAL[urlRail]!
      : "overview";

  const decideSubview: DecideSubview =
    normalizeFunderDecideSubview(urlRail) ?? "incremental-evals";

  const urlItemId = params?.itemId ?? null;
  const selectedProposalId =
    workspaceTab === "review" && railMode === "orgs" ? urlItemId : null;
  const selectedRecommenderId =
    workspaceTab === "review" && railMode === "recs" ? urlItemId : null;
  const selectedTrackId =
    workspaceTab === "review" && railMode === "tracks" ? urlItemId : null;

  // Per-tab memory of last (rail, itemId), so flipping tabs preserves the
  // user's place in each side. The URL only encodes one tab's state at a
  // time, so the other tab's state has to live outside the URL.
  const tabStateRef = useRef<{
    review: { rail: RailMode; itemId: string | null };
    decide: { subview: DecideSubview; itemId: string | null };
  }>({
    review: { rail: "overview", itemId: null },
    decide: { subview: "incremental-evals", itemId: null },
  });
  useEffect(() => {
    if (workspaceTab === "review") {
      tabStateRef.current.review = { rail: railMode, itemId: urlItemId };
    } else {
      tabStateRef.current.decide = {
        subview: decideSubview,
        itemId: urlItemId,
      };
    }
  }, [workspaceTab, railMode, decideSubview, urlItemId]);

  const setWorkspaceTab = useCallback(
    (value: WorkspaceTab) => {
      if (value === "review") {
        const remembered = tabStateRef.current.review;
        const railSeg = INTERNAL_RAIL_TO_URL[remembered.rail];
        const path =
          remembered.itemId == null
            ? `/funder/review/${railSeg}`
            : `/funder/review/${railSeg}/${remembered.itemId}`;
        setLocation(path);
      } else {
        const remembered = tabStateRef.current.decide;
        const path =
          remembered.itemId == null
            ? `/funder/decide/${remembered.subview}`
            : `/funder/decide/${remembered.subview}/${remembered.itemId}`;
        setLocation(path);
      }
    },
    [setLocation],
  );
  const buildRailItemPath = useCallback(
    (rail: RailMode, itemId: string | null) => {
      const base = `/funder/review/${INTERNAL_RAIL_TO_URL[rail]}`;
      return itemId == null ? base : `${base}/${itemId}`;
    },
    [],
  );
  const setRailMode = useCallback(
    (value: RailMode, itemId: string | null = null) =>
      setLocation(buildRailItemPath(value, itemId)),
    [setLocation, buildRailItemPath],
  );
  const setDecideSubview = useCallback(
    (value: DecideSubview) => setLocation(`/funder/decide/${value}`),
    [setLocation],
  );
  // Append a search-hit highlight to a rail-item path: `?quote=<term>` for the
  // destination panel to highlight, plus a one-shot `?searchHit=` token so
  // re-clicking the same result re-triggers the highlight effect (mirrors the
  // transcript deep link's `searchHit`).
  const withQuote = useCallback((path: string, quote?: string | null) => {
    const trimmed = quote?.trim();
    if (!trimmed) return path;
    const params = new URLSearchParams({
      quote: trimmed,
      searchHit: Date.now().toString(36),
    });
    return `${path}?${params}`;
  }, []);
  const setSelectedProposalId = useCallback(
    (
      value: string | null,
      options?: { replace?: boolean; quote?: string | null },
    ) =>
      setLocation(withQuote(buildRailItemPath("orgs", value), options?.quote), {
        replace: options?.replace,
      }),
    [setLocation, buildRailItemPath, withQuote],
  );
  const setSelectedRecommenderId = useCallback(
    (value: string | null, options?: { replace?: boolean }) =>
      setLocation(buildRailItemPath("recs", value), options),
    [setLocation, buildRailItemPath],
  );
  // Open a recommender anchored on one proposal — the recommender×proposal
  // cross, matching `funderEntityPath`'s `?proposal=` convention.
  const setSelectedRecommenderProposal = useCallback(
    (
      recommenderId: string,
      proposalId: string,
      options?: { quote?: string | null },
    ) => {
      const params = new URLSearchParams({ proposal: proposalId });
      const quote = options?.quote?.trim();
      if (quote) params.set("quote", quote);
      // One-shot nonce (mirrors `withQuote`) so re-landing on the same proposal
      // in an already-mounted recommender view re-triggers the row scroll /
      // filter-clear — the eval-modal scroll latch keys on it. A Back/Forward
      // restore replays the same nonce and stays suppressed.
      params.set("searchHit", Date.now().toString(36));
      setLocation(`${buildRailItemPath("recs", recommenderId)}?${params}`);
    },
    [setLocation, buildRailItemPath],
  );
  const setSelectedTrackId = useCallback(
    (
      value: string | null,
      options?: { replace?: boolean; quote?: string | null },
    ) =>
      setLocation(
        withQuote(buildRailItemPath("tracks", value), options?.quote),
        { replace: options?.replace },
      ),
    [setLocation, buildRailItemPath, withQuote],
  );

  return {
    workspaceTab,
    setWorkspaceTab,
    railMode,
    setRailMode,
    decideSubview,
    setDecideSubview,
    selectedProposalId,
    setSelectedProposalId,
    selectedRecommenderId,
    setSelectedRecommenderId,
    setSelectedRecommenderProposal,
    selectedTrackId,
    setSelectedTrackId,
  };
}
